import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Link } from "react-router-dom";
import PublicFooter from "@/components/PublicFooter";
import { toast } from "sonner";
import { CheckCircle, FileArrowUp, GlobeHemisphereWest } from "@phosphor-icons/react";

const LOGO_URL = "https://customer-assets-gfyr7b9c.emergentagent.net/job_francophone-ops/artifacts/1lh4l6xi_3%20%281%29.PNG";

const publicApi = axios.create({ baseURL: API });

const empty = {
  full_name: "", email: "", phone: "",
  address: "", city_state_zip: "", ssn_ein: "", max_travel_miles: "",
  country: "United States", tax_form_type: "w9",
  payment_methods: [], payment_details: {},
  language_pairs_raw: "", specialties_raw: "",
  hourly_rate: "", opi_rate: "", opi_unit: "hour", vri_rate: "", vri_unit: "hour", translation_rate: "",
  location: "", notes: "",
};

export default function ApplyInterpreter() {
  const [company, setCompany] = useState({ company_name: "Unity Language Group" });
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState("form"); // form | w9 | done
  const [applicationId, setApplicationId] = useState(null);
  const [w9File, setW9File] = useState(null);
  const [agree, setAgree] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    publicApi.get("/public/settings").then((r) => setCompany(r.data)).catch(() => {});
  }, []);

  // Parse a comma-separated free-text field into a clean array (used at submit time).
  const splitCsv = (s) => (s || "").split(",").map((x) => x.trim()).filter(Boolean);

  const submit = async () => {
    if (!form.full_name || !form.email) return toast.error("Name and email required");
    const language_pairs = splitCsv(form.language_pairs_raw);
    const specialties = splitCsv(form.specialties_raw);
    if (!language_pairs.length) return toast.error("Add at least one language pair");
    if (!agree) return toast.error("Please accept the Privacy Policy and Terms of Service to continue");
    setSubmitting(true);
    try {
      const numeric = (v) => (v === "" || v === null || v === undefined ? 0 : parseFloat(v) || 0);
      const payload = {
        ...form,
        language_pairs,
        specialties,
        hourly_rate: numeric(form.hourly_rate),
        opi_rate: numeric(form.opi_rate),
        vri_rate: numeric(form.vri_rate),
        translation_rate: numeric(form.translation_rate),
        max_travel_miles: form.max_travel_miles ? parseInt(form.max_travel_miles, 10) : null,
      };
      // Strip the raw string mirrors before sending — backend doesn't know them.
      delete payload.language_pairs_raw;
      delete payload.specialties_raw;
      const r = await publicApi.post("/public/interpreter-applications", payload);
      setApplicationId(r.data.interpreter_id);
      setStep("w9");
      toast.success("Application received");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed");
    } finally { setSubmitting(false); }
  };

  const uploadW9 = async () => {
    if (!w9File) return toast.error("Select a file");
    const fd = new FormData();
    fd.append("file", w9File);
    try {
      await publicApi.post(`/public/interpreter-applications/${applicationId}/w9`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setStep("done");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="apply-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-6 py-5 flex items-center gap-3">
          <img src={LOGO_URL} alt="" className="h-10 w-10 object-contain" />
          <div>
            <div className="font-display font-bold text-slate-900 text-lg">{company.company_name}</div>
            <div className="text-[11px] uppercase tracking-[0.15em] text-[#B91C1C] font-semibold">Interpreter Application</div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10">
        {step === "form" && (
          <>
            <h1 className="text-3xl sm:text-4xl font-display font-bold text-slate-900 tracking-tight">Join our francophone interpreter roster</h1>
            <p className="text-slate-600 mt-3 leading-relaxed">
              Serve francophone clients in USCIS, medical, and legal proceedings across the US.
              Submit the form below — approved applicants receive assignments as they come in.
            </p>

            <div className="mt-8 card-flat p-6 space-y-5" data-testid="apply-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <F label="Full name *"><Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="apply-name" /></F>
                <F label="Email *"><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="apply-email" /></F>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <F label="Phone"><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></F>
                <F label="Current location (city, state)"><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></F>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <F label="Street address"><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="apply-address" /></F>
                <F label="City, State, ZIP"><Input value={form.city_state_zip} onChange={(e) => setForm({ ...form, city_state_zip: e.target.value })} data-testid="apply-city-state-zip" /></F>
              </div>
              <F label="Max distance willing to travel (miles)">
                <Input
                  type="number"
                  min="0"
                  value={form.max_travel_miles}
                  onChange={(e) => setForm({ ...form, max_travel_miles: e.target.value })}
                  data-testid="apply-max-travel-miles"
                />
                <div className="text-[11px] text-slate-500 mt-1">
                  Leave blank if you only accept remote (phone/video) assignments.
                </div>
              </F>
              <F label="Country">
                <select
                  value={form.country}
                  onChange={(e) => setForm({ ...form, country: e.target.value, tax_form_type: e.target.value === "United States" ? "w9" : "w8ben" })}
                  data-testid="apply-country"
                  className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm"
                >
                  <optgroup label="Common">
                    <option>United States</option><option>Haiti</option><option>Canada</option><option>France</option>
                    <option>Belgium</option><option>Côte d'Ivoire</option><option>Cameroon</option><option>Senegal</option>
                    <option>Democratic Republic of the Congo</option><option>Morocco</option><option>Tunisia</option>
                    <option>Dominican Republic</option><option>Mexico</option>
                  </optgroup>
                  <optgroup label="Other">
                    <option>Algeria</option><option>Argentina</option><option>Australia</option><option>Brazil</option>
                    <option>Chile</option><option>China</option><option>Colombia</option><option>Egypt</option>
                    <option>Ethiopia</option><option>Germany</option><option>Ghana</option><option>India</option>
                    <option>Italy</option><option>Kenya</option><option>Nigeria</option><option>Peru</option>
                    <option>Philippines</option><option>Poland</option><option>Portugal</option><option>Rwanda</option>
                    <option>South Africa</option><option>Spain</option><option>Switzerland</option><option>Togo</option>
                    <option>United Kingdom</option><option>Venezuela</option><option>Other</option>
                  </optgroup>
                </select>
              </F>

              {form.country === "United States" ? (
                <F label="SSN or EIN (for W-9)"><Input value={form.ssn_ein} onChange={(e) => setForm({ ...form, ssn_ein: e.target.value })} data-testid="apply-ssn-ein" /></F>
              ) : (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                  <b>Non-US contractor:</b> Instead of a W-9, please upload IRS Form <b>W-8BEN</b> (foreign individuals) or <b>W-8BEN-E</b> (foreign entities) on the next screen. Download the blank form at <a className="underline" href="https://www.irs.gov/forms-pubs/about-form-w-8-ben" target="_blank" rel="noreferrer">irs.gov/w-8ben</a>.
                </div>
              )}

              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Payment methods you can receive</div>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    ["zelle", "Zelle (US only)"], ["paypal", "PayPal"], ["wise", "Wise"],
                    ["wire", "Wire transfer / SWIFT"], ["remitly", "Remitly"],
                    ["check", "Check by mail (US only)"], ["ach", "ACH direct deposit (US only)"]
                  ].map(([k, label]) => (
                    <label key={k} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        data-testid={`apply-pm-${k}`}
                        checked={form.payment_methods.includes(k)}
                        onChange={(e) => {
                          const set = new Set(form.payment_methods);
                          e.target.checked ? set.add(k) : set.delete(k);
                          setForm({ ...form, payment_methods: [...set] });
                        }}
                      />
                      {label}
                    </label>
                  ))}
                </div>

                <div className="mt-3 space-y-3">
                  {form.payment_methods.includes("paypal") && (
                    <F label="PayPal email"><Input data-testid="apply-paypal-email" value={form.payment_details.paypal_email || ""} onChange={(e) => setForm({ ...form, payment_details: { ...form.payment_details, paypal_email: e.target.value } })} /></F>
                  )}
                  {form.payment_methods.includes("zelle") && (
                    <F label="Zelle (email or phone)"><Input data-testid="apply-zelle" value={form.payment_details.zelle || ""} onChange={(e) => setForm({ ...form, payment_details: { ...form.payment_details, zelle: e.target.value } })} /></F>
                  )}
                  {form.payment_methods.includes("wise") && (
                    <F label="Wise account (email or Wise-tag)"><Input data-testid="apply-wise" value={form.payment_details.wise || ""} onChange={(e) => setForm({ ...form, payment_details: { ...form.payment_details, wise: e.target.value } })} /></F>
                  )}
                  {form.payment_methods.includes("wire") && (
                    <F label="Wire / SWIFT details (bank name, SWIFT/BIC, IBAN or account #, holder name)">
                      <Textarea rows={3} data-testid="apply-wire" value={form.payment_details.wire || ""} onChange={(e) => setForm({ ...form, payment_details: { ...form.payment_details, wire: e.target.value } })} />
                    </F>
                  )}
                  {form.payment_methods.includes("remitly") && (
                    <F label="Remitly contact (email/phone) + destination country"><Input data-testid="apply-remitly" value={form.payment_details.remitly || ""} onChange={(e) => setForm({ ...form, payment_details: { ...form.payment_details, remitly: e.target.value } })} /></F>
                  )}
                  {form.payment_methods.includes("ach") && (
                    <F label="ACH — routing # + account # + account holder name">
                      <Textarea rows={2} data-testid="apply-ach" value={form.payment_details.ach || ""} onChange={(e) => setForm({ ...form, payment_details: { ...form.payment_details, ach: e.target.value } })} />
                    </F>
                  )}
                  {form.payment_methods.includes("check") && (
                    <F label="Mailing address for check (if different from above)"><Input data-testid="apply-check-address" value={form.payment_details.check_address || ""} onChange={(e) => setForm({ ...form, payment_details: { ...form.payment_details, check_address: e.target.value } })} /></F>
                  )}
                </div>
              </div>

              <div className="pt-2 border-t border-slate-200">
                <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 font-semibold mb-3">Languages & specialties</div>
                <F label="Language pairs (comma-separated)">
                  <Input
                    value={form.language_pairs_raw}
                    onChange={(e) => setForm({ ...form, language_pairs_raw: e.target.value })}
                    autoCapitalize="words"
                    autoCorrect="off"
                    spellCheck={false}
                    inputMode="text"
                    data-testid="apply-languages"
                  />
                </F>
                <div className="mt-4">
                  <F label="Specialties (comma-separated)">
                    <Input
                      value={form.specialties_raw}
                      onChange={(e) => setForm({ ...form, specialties_raw: e.target.value })}
                      autoCapitalize="words"
                      autoCorrect="off"
                      spellCheck={false}
                      inputMode="text"
                      data-testid="apply-specialties"
                    />
                  </F>
                </div>
              </div>

              <div className="pt-2 border-t border-slate-200">
                <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 font-semibold mb-3">Your rates</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <F label="In-person ($/hour)">
                    <Input
                      type="number" step="0.01" min="0" inputMode="decimal"
                      value={form.hourly_rate}
                      onChange={(e) => setForm({ ...form, hourly_rate: e.target.value })}
                      onFocus={(e) => e.target.select()}
                      data-testid="apply-inperson"
                    />
                  </F>
                  <F label="Translation ($/word)">
                    <Input
                      type="number" step="0.0001" min="0" inputMode="decimal"
                      value={form.translation_rate}
                      onChange={(e) => setForm({ ...form, translation_rate: e.target.value })}
                      onFocus={(e) => e.target.select()}
                      data-testid="apply-translation"
                    />
                  </F>
                  <F label={`OPI phone ($/${form.opi_unit === "min" ? "minute" : "hour"})`}>
                    <div className="flex gap-2">
                      <Input
                        type="number" step="0.01" min="0" inputMode="decimal"
                        value={form.opi_rate}
                        onChange={(e) => setForm({ ...form, opi_rate: e.target.value })}
                        onFocus={(e) => e.target.select()}
                        data-testid="apply-opi"
                        className="flex-1"
                      />
                      <select
                        value={form.opi_unit}
                        onChange={(e) => setForm({ ...form, opi_unit: e.target.value })}
                        data-testid="apply-opi-unit"
                        className="border border-slate-300 rounded-md px-2 text-sm bg-white"
                      >
                        <option value="hour">/ hour</option>
                        <option value="min">/ minute</option>
                      </select>
                    </div>
                  </F>
                  <F label={`VRI video ($/${form.vri_unit === "min" ? "minute" : "hour"})`}>
                    <div className="flex gap-2">
                      <Input
                        type="number" step="0.01" min="0" inputMode="decimal"
                        value={form.vri_rate}
                        onChange={(e) => setForm({ ...form, vri_rate: e.target.value })}
                        onFocus={(e) => e.target.select()}
                        data-testid="apply-vri"
                        className="flex-1"
                      />
                      <select
                        value={form.vri_unit}
                        onChange={(e) => setForm({ ...form, vri_unit: e.target.value })}
                        data-testid="apply-vri-unit"
                        className="border border-slate-300 rounded-md px-2 text-sm bg-white"
                      >
                        <option value="hour">/ hour</option>
                        <option value="min">/ minute</option>
                      </select>
                    </div>
                  </F>
                </div>
              </div>

              <F label="Anything else you'd like us to know?"><Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></F>

              <label className="flex items-start gap-2 pt-2 cursor-pointer">
                <Checkbox
                  data-testid="apply-agree"
                  checked={agree}
                  onCheckedChange={(v) => setAgree(!!v)}
                  className="mt-0.5"
                />
                <span className="text-xs text-slate-700 leading-relaxed">
                  I have read and agree to Unity Language Group's{" "}
                  <Link to="/privacy" target="_blank" className="text-blue-700 underline">Privacy Policy</Link>
                  {" "}and{" "}
                  <Link to="/terms" target="_blank" className="text-blue-700 underline">Terms of Service</Link>.
                  I understand that if approved I will be an independent contractor (1099), not an employee,
                  and that Unity will retain my W-9 to issue Form 1099-NEC where required by law.
                </span>
              </label>

              <div className="pt-2">
                <Button onClick={submit} disabled={submitting || !agree} className="w-full h-11 bg-[#1E3A8A] hover:bg-[#1E40AF] text-white" data-testid="apply-submit">
                  {submitting ? "Submitting…" : (form.tax_form_type === "w8ben" ? "Continue to W-8BEN upload →" : "Continue to W-9 upload →")}
                </Button>
              </div>
            </div>
          </>
        )}

        {step === "w9" && (
          <div className="card-flat p-8" data-testid="apply-w9-step">
            <h1 className="text-3xl font-display font-bold text-slate-900">
              One more step — upload your {form.tax_form_type === "w8ben" ? "W-8BEN" : "W-9"}
            </h1>
            <p className="text-slate-600 mt-2">
              {form.tax_form_type === "w8ben"
                ? <>Non-US contractors: upload a completed IRS Form W-8BEN (individuals) or W-8BEN-E (entities). Download the blank form at <a className="underline text-blue-700" href="https://www.irs.gov/forms-pubs/about-form-w-8-ben" target="_blank" rel="noreferrer">irs.gov/w-8ben</a>. Applications without a tax form on file cannot receive payments.</>
                : "Applications without a signed W-9 on file cannot receive assignments. Upload a PDF or clear image."}
            </p>
            <input ref={fileRef} type="file" accept=".pdf,image/png,image/jpeg,image/webp" onChange={(e) => setW9File(e.target.files?.[0] || null)} className="hidden" data-testid="apply-w9-input" />
            <div className="mt-6 space-y-3">
              <Button onClick={() => fileRef.current?.click()} variant="outline" className="w-full h-11" data-testid="apply-w9-pick">
                <FileArrowUp size={16} className="mr-2" /> {w9File ? w9File.name : `Choose ${form.tax_form_type === "w8ben" ? "W-8BEN" : "W-9"} file (PDF/PNG/JPG)`}
              </Button>
              <Button onClick={uploadW9} disabled={!w9File} className="w-full h-11 bg-[#1E3A8A] hover:bg-[#1E40AF] text-white" data-testid="apply-w9-submit">
                Upload & finish
              </Button>
              <button onClick={() => setStep("done")} className="w-full text-xs text-slate-500 underline mt-2">Skip for now (I'll email it later)</button>
            </div>
          </div>
        )}

        {step === "done" && (
          <div className="card-flat p-10 text-center" data-testid="apply-done">
            <CheckCircle size={56} weight="duotone" className="mx-auto text-emerald-600" />
            <h1 className="text-3xl font-display font-bold text-slate-900 mt-4">Thanks — you're on our list</h1>
            <p className="text-slate-600 mt-3 max-w-md mx-auto">
              We'll review your application within 2 business days and email you at <b>{form.email}</b>.
              For questions, reach us at {company.company_email || "info@unitylanguagegroup.com"}.
            </p>
          </div>
        )}

        <div className="mt-8 text-center text-xs text-slate-400 flex items-center justify-center gap-2">
          <GlobeHemisphereWest size={12} /> {company.company_name} · USCIS · Medical · Legal Interpretation
        </div>
        <PublicFooter />
      </main>
    </div>
  );
}

function F({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
